/**
 * useUnsavedChanges Hook
 *
 * Tracks unsaved changes in survey forms across all survey types
 * (Custom, Event Registration, Satisfaction, Donation)
 *
 * ## Features
 * - Monitors React Hook Form's isDirty flag for basic form changes
 * - Supports additional custom conditions for survey-specific changes
 * - Prevents accidental browser navigation with beforeunload listener
 * - Only shows warnings in edit mode (not during survey creation)
 * - Proper cleanup of event listeners to prevent memory leaks
 *
 * ## How it Works
 * 1. Tracks React Hook Form's isDirty and dirtyFields state
 *    - isDirty: Becomes true when any form field value changes
 *    - dirtyFields: Fallback for cases where setValue() was used instead of reset()
 *
 * 2. Evaluates additional custom conditions passed in
 *    - Custom form field changes (via hasCustomFormChanges utility)
 *    - Event-specific changes (RSVP settings, images)
 *    - Satisfaction metadata changes (conditional questions)
 *
 * 3. Shows native browser confirmation when navigating away
 *    - Only triggers when editing existing surveys
 *    - Prevents accidental data loss from closing tab/window
 *
 * ## Performance
 * - Memoized with React.useMemo to prevent unnecessary recalculations
 * - Dependencies: isDirty, dirtyFields, isClone, additionalConditions
 * - O(1) complexity for basic checks, additional conditions are pre-computed
 *
 * ## Browser Navigation Prevention
 * When unsaved changes exist and user attempts to leave:
 * - Shows native browser confirmation dialog
 * - Message content controlled by browser (security requirement)
 * - Works with: closing tab, refreshing page, navigating to new URL
 * - Does NOT work with: in-app navigation (handled separately by component)
 */

import * as React from 'react';
import type { FormState } from 'react-hook-form';

/**
 * Options for useUnsavedChanges hook
 */
export interface UseUnsavedChangesOptions {
	/**
	 * Survey type - used for type-specific change detection
	 * Currently supports: 'custom' | 'event' | 'satisfaction' | 'donation'
	 *
	 * While not actively used in the hook logic, this parameter:
	 * - Documents which survey type is using the hook
	 * - Reserved for future survey-specific behavior
	 * - Helps with debugging and logging
	 */
	surveyType: 'custom' | 'event' | 'satisfaction' | 'donation';

	/**
	 * Whether the survey is being edited (vs created)
	 *
	 * When true:
	 * - Warning banner displays when changes detected
	 * - Browser navigation prevention is active
	 *
	 * When false (new survey creation):
	 * - No warnings shown (nothing to "unsave" yet)
	 * - Browser navigation works normally
	 */
	isEdit: boolean;

	/**
	 * Whether the survey is being cloned
	 *
	 * When true:
	 * - Always returns true for hasUnsavedChanges
	 * - Ensures "Save" creates a new survey copy
	 * - Prevents accidental overwrite of original
	 */
	isClone?: boolean;

	/**
	 * React Hook Form's formState object
	 *
	 * Contains:
	 * - isDirty: True when any field differs from default values
	 * - dirtyFields: Object tracking which specific fields changed
	 *
	 * Note: Due to form initialization with setValue(), dirtyFields
	 * may be empty even when changes exist, so we check both.
	 */
	formState: FormState<any>;

	/**
	 * Additional custom conditions to check for unsaved changes
	 *
	 * Array of boolean conditions that are OR'd together.
	 * Used for survey-specific changes not tracked by React Hook Form:
	 *
	 * Examples:
	 * - Custom form field changes: hasCustomFormChanges(original, current)
	 * - Satisfaction metadata: customFormMetaDataHasChanges
	 * - Event RSVP settings: formValues.enableRSVP !== eventData?.rsvpEnabled
	 * - Image changes: state.selectedImage?.url !== eventData?.image?.url
	 *
	 * All conditions are evaluated every render (should be memoized).
	 */
	additionalConditions?: boolean[];
}

/**
 * Hook to track unsaved changes in survey forms
 *
 * Returns true when:
 * - Survey is being edited (not created)
 * - AND any of these conditions are true:
 *   - isClone is true (always show for cloned surveys)
 *   - React Hook Form isDirty flag is true
 *   - React Hook Form has dirty fields
 *   - Any additional condition returns true
 *
 * @param options - Configuration options (see UseUnsavedChangesOptions)
 * @returns boolean - True if warning should be displayed and browser navigation blocked
 *
 * @example Basic usage with custom form changes
 * ```tsx
 * const customFormFieldsHaveChanges = React.useMemo(
 *   () => hasCustomFormChanges(survey?.customForm?.fields, formValues.customForm?.fields),
 *   [survey?.customForm?.fields, formValues.customForm?.fields]
 * );
 *
 * const hasUnsavedChanges = useUnsavedChanges({
 *   surveyType: 'custom',
 *   isEdit: true,
 *   isClone: false,
 *   formState,
 *   additionalConditions: [customFormFieldsHaveChanges],
 * });
 * ```
 *
 * @example Event survey with multiple conditions
 * ```tsx
 * const hasUnsavedChanges = useUnsavedChanges({
 *   surveyType: 'event',
 *   isEdit: true,
 *   isClone: false,
 *   formState,
 *   additionalConditions: [
 *     customFormFieldsHaveChanges,
 *     formValues.enableRequestAttendeesToRSVP !== eventData?.attendeeOptions?.enabled,
 *     state.selectedImage?.url !== eventData?.eventInformation?.image?.url,
 *   ]
 * });
 * ```
 *
 * @example Satisfaction survey with metadata tracking
 * ```tsx
 * const hasUnsavedChanges = useUnsavedChanges({
 *   surveyType: 'satisfaction',
 *   isEdit: true,
 *   isClone: false,
 *   formState,
 *   additionalConditions: [
 *     customFormFieldsHaveChanges,
 *     customFormMetaDataHasChanges, // Tracks conditional question settings
 *   ]
 * });
 * ```
 */
export const useUnsavedChanges = (options: UseUnsavedChangesOptions): boolean => {
	const { surveyType: _surveyType, isEdit, isClone = false, formState, additionalConditions = [] } = options;

	// Memoize the unsaved changes calculation to avoid unnecessary recalculations
	// This runs on every render but only recalculates when dependencies change
	const hasUnsavedChanges = React.useMemo(() => {
		// Clone mode: Always show warning to prevent accidental overwrite
		if (isClone) {
			return true;
		}

		// Check React Hook Form's isDirty flag
		// Becomes true when any form field value differs from default values
		const isFormDirty = formState.isDirty;

		// Also check dirtyFields directly as a fallback
		// This handles edge cases where forms are initialized with setValue()
		// instead of reset(), which can cause isDirty to remain false
		const hasDirtyFields = formState.dirtyFields ? Object.keys(formState.dirtyFields).length > 0 : false;

		// Check additional custom conditions (survey-specific changes)
		// These conditions are pre-computed and memoized by the caller
		// Examples: custom form field changes, metadata changes, image changes
		const hasAdditionalChanges = additionalConditions.some(condition => condition === true);

		// Return true if ANY condition indicates changes
		// This is an OR operation across all change detection mechanisms
		return isFormDirty || hasDirtyFields || hasAdditionalChanges;
	}, [formState.isDirty, formState.dirtyFields, isClone, additionalConditions]);

	// Browser navigation prevention
	// Shows native browser confirmation dialog when user tries to leave page
	React.useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			// Only prevent navigation when:
			// 1. Editing existing survey (not creating new one)
			// 2. Unsaved changes exist
			if (isEdit && hasUnsavedChanges) {
				// Prevent default behavior (page unload)
				e.preventDefault();

				// Set returnValue to trigger browser's native confirmation dialog
				// Modern browsers ignore custom messages and show standard dialog
				// Message typically: "Changes you made may not be saved."
				e.returnValue = '';
			}
		};

		// Register event listener for browser navigation/close attempts
		window.addEventListener('beforeunload', handleBeforeUnload);

		// Cleanup: Remove listener when component unmounts or dependencies change
		// Critical for preventing memory leaks and stale listeners
		return () => window.removeEventListener('beforeunload', handleBeforeUnload);
	}, [isEdit, hasUnsavedChanges]);

	// Final return value determines if warning banner should display
	// Only show during edit mode - new survey creation doesn't need warnings
	return isEdit && hasUnsavedChanges;
};
